import React, { useEffect, useId, useRef, useState } from 'react';
const OptionCheckbox = ({ checked, label, name, onChange, singleSelect }) => {
    return (React.createElement("label", { className: "flex min-w-0 flex-1 cursor-pointer items-center text-base font-normal text-gray-900" },
        React.createElement("input", { type: singleSelect ? 'radio' : 'checkbox', name: singleSelect ? name : undefined, className: "mr-2 h-5 w-5 flex-none border-gray-300", checked: checked, onChange: onChange }),
        React.createElement("span", { className: "truncate" }, label)));
};
const findOptionLabel = (options, value) => {
    for (const option of options) {
        if (option.value === value) {
            return option.label;
        }
        const childLabel = findOptionLabel(option.children || [], value);
        if (childLabel) {
            return childLabel;
        }
    }
    return null;
};
const GroupedMultiSelect = ({ label, options, selectedValues, onChange, placeholder, formatSelectedCount, disabled = false, singleSelect = false, onSelectAll, onClearAll }) => {
    const [isOpen, setOpen] = useState(false);
    const containerRef = useRef(null);
    const id = useId();
    const labelId = `${id}-label`;
    const triggerId = `${id}-trigger`;
    const triggerTextId = `${id}-trigger-text`;
    const isDisabled = disabled || !options.length;
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (containerRef.current &&
                !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    const toggleOption = (option) => {
        if (singleSelect) {
            onChange([option.value]);
            setOpen(false);
            return;
        }
        const nextValues = new Set(selectedValues);
        if (nextValues.has(option.value)) {
            nextValues.delete(option.value);
        }
        else {
            nextValues.add(option.value);
        }
        onChange(Array.from(nextValues));
    };
    const renderOption = (option, depth = 0) => {
        var _a, _b;
        const hasChildren = Boolean((_a = option.children) === null || _a === void 0 ? void 0 : _a.length);
        const isChecked = selectedValues.includes(option.value);
        return (React.createElement("div", { key: option.value, role: "treeitem", "aria-selected": isChecked, "aria-expanded": hasChildren ? true : undefined },
            React.createElement("div", { className: "mb-3 flex items-center justify-between", style: { paddingLeft: `${depth * 12}px` } },
                React.createElement(OptionCheckbox, { checked: isChecked, label: option.label, name: `${id}-option`, onChange: () => toggleOption(option), singleSelect: singleSelect })),
            hasChildren && (React.createElement("div", { role: "group" }, (_b = option.children) === null || _b === void 0 ? void 0 : _b.map((child) => {
                return renderOption(child, depth + 1);
            })))));
    };
    const selectedLabel = singleSelect
        ? findOptionLabel(options, selectedValues[0])
        : null;
    const triggerText = selectedValues.length
        ? selectedLabel ||
            (formatSelectedCount
                ? formatSelectedCount(selectedValues.length)
                : `${label} (${selectedValues.length})`)
        : placeholder || label;
    return (React.createElement("div", { ref: containerRef, className: "relative w-full" },
        React.createElement("label", { id: labelId, htmlFor: triggerId, className: "font-semibold text-gray-800 text-lg" }, label),
        React.createElement("button", { id: triggerId, type: "button", onClick: () => setOpen((open) => !open), disabled: isDisabled, className: `mt-2 w-full rounded-sm border border-gray-300 p-3 text-left text-base font-normal text-gray-700 transition-all focus:border-blue-500 focus:outline-hidden ${isDisabled
                ? 'cursor-not-allowed bg-gray-200'
                : 'cursor-pointer bg-white hover:bg-gray-50'}`, "aria-haspopup": "tree", "aria-expanded": isOpen, "aria-labelledby": `${labelId} ${triggerTextId}` },
            React.createElement("span", { className: "flex items-center justify-between" },
                React.createElement("span", { id: triggerTextId }, triggerText),
                React.createElement("span", { className: "material-icons text-base" }, isOpen ? 'expand_less' : 'expand_more'))),
        isOpen && (React.createElement("div", { className: "absolute z-20 mt-2 max-h-80 w-full overflow-auto rounded-md border border-solid border-gray-200 bg-white px-5 py-5 shadow-2xl", role: "tree", "aria-label": label },
            (onSelectAll || onClearAll) && (React.createElement("div", { className: "mb-4 flex items-center justify-between border-b border-gray-200 pb-3" },
                onSelectAll ? (React.createElement("button", { type: "button", className: "text-sm font-semibold text-blue-600 hover:text-blue-800", onClick: onSelectAll }, "Select all")) : (React.createElement("span", null)),
                onClearAll && (React.createElement("button", { type: "button", className: "text-sm font-semibold text-gray-600 hover:text-gray-800 disabled:cursor-not-allowed disabled:text-gray-300", onClick: onClearAll, disabled: selectedValues.length === 0 }, "Clear all")))),
            options.map((option) => renderOption(option))))));
};
export default GroupedMultiSelect;
